import React, { createContext, useContext, useState, useEffect } from 'react'
import { realProperties } from '../data/realProperties'
import { apiService, getAuthToken } from '../services/apiService'

const PGManagementContext = createContext(null)

export const PGManagementProvider = ({ children }) => {
  // 1. Core State: Live API is the Single Source of Truth (SOT)
  const [properties, setProperties] = useState(realProperties)
  const [applicants, setApplicants] = useState([])
  const [residents, setResidents] = useState([])
  const [invoices, setInvoices] = useState([])
  const [receipts, setReceipts] = useState([])
  const [complaints, setComplaints] = useState([])

  const [settlements, setSettlements] = useState([])
  const [notifications, setNotifications] = useState([])
  const [activeResidentId, setActiveResidentId] = useState('')
  const [isDataLoading, setIsDataLoading] = useState(false)

  // Helper: Find Active Resident Object strictly from authenticated session user
  const authSessionUser = (() => {
    try {
      const raw = localStorage.getItem('ch24_auth_session')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })()

  // Fetch live records from backend API (Single Source of Truth)
  const refreshData = async () => {
    setIsDataLoading(true)
    try {
      // 1. Bed matrix & inventory is public
      const bedsRes = await apiService.getBeds()
      if (bedsRes?.success && Array.isArray(bedsRes.data) && bedsRes.data.length > 0) {
        setProperties(bedsRes.data)
      }

      // 2. Protected records require an active authentication session
      const token = getAuthToken()
      if (!token) {
        setApplicants([])
        setResidents([])
        setInvoices([])
        setReceipts([])
        setComplaints([])
        setSettlements([])
        return
      }

      const isAdmin = authSessionUser?.role === 'admin'
      const residentId = authSessionUser?.id || authSessionUser?.userId || null

      if (isAdmin) {
        // Admin: Load full management suite in parallel
        const [appsRes, resRes, invRes, recRes, compRes, setRes, notifRes] = await Promise.all([
          apiService.getApplications(),
          apiService.getResidents(),
          apiService.getInvoices(),
          apiService.getReceipts(),
          apiService.getComplaints(),
          apiService.getSettlements(),
          apiService.getNotifications()
        ])

        if (appsRes?.success && Array.isArray(appsRes.data)) setApplicants(appsRes.data)
        if (resRes?.success && Array.isArray(resRes.data)) setResidents(resRes.data)
        if (invRes?.success && Array.isArray(invRes.data)) setInvoices(invRes.data)
        if (recRes?.success && Array.isArray(recRes.data)) setReceipts(recRes.data)
        if (compRes?.success && Array.isArray(compRes.data)) setComplaints(compRes.data)
        if (setRes?.success && Array.isArray(setRes.data)) setSettlements(setRes.data)
        if (notifRes?.success && Array.isArray(notifRes.data)) setNotifications(notifRes.data)
      } else if (residentId) {
        // Resident: Load scoped resident records only
        const [resRes, invRes, recRes, compRes, notifRes] = await Promise.all([
          apiService.getResidents(residentId),
          apiService.getInvoices(residentId),
          apiService.getReceipts(residentId),
          apiService.getComplaints(residentId),
          apiService.getNotifications()
        ])

        if (resRes?.success && resRes.data) {
          setResidents(Array.isArray(resRes.data) ? resRes.data : [resRes.data])
        }
        if (invRes?.success && Array.isArray(invRes.data)) setInvoices(invRes.data)
        if (recRes?.success && Array.isArray(recRes.data)) setReceipts(recRes.data)
        if (compRes?.success && Array.isArray(compRes.data)) setComplaints(compRes.data)
        if (notifRes?.success && Array.isArray(notifRes.data)) setNotifications(notifRes.data)
      }
    } catch (err) {
      console.warn('Live API sync notice:', err)
    } finally {
      setIsDataLoading(false)
    }
  }

  // Initial load & when user session changes
  useEffect(() => {
    refreshData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSessionUser?.id, authSessionUser?.role, authSessionUser?.token])

  const currentResident = 
    (authSessionUser?.id ? residents.find(r => r.id === authSessionUser.id || (r.phone && r.phone === authSessionUser.phone) || (r.email && r.email === authSessionUser.email)) : null) ||
    (activeResidentId ? residents.find(r => r.id === activeResidentId) : null) || 
    null

  // Helper: Reset all beds across all societies to AVAILABLE
  const resetAllBedsToAvailable = (propsList) => {
    return (propsList || realProperties).map(prop => ({
      ...prop,
      flats: (prop.flats || []).map(flat => ({
        ...flat,
        rooms: (flat.rooms || []).map(room => ({
          ...room,
          beds: (room.beds || []).map(bed => ({
            ...bed,
            status: 'AVAILABLE',
            residentId: null,
            residentName: null,
            residentPhone: null
          }))
        }))
      }))
    }))
  }

  // Complete Clean-Slate: Purge all operational data
  const clearAllData = () => {
    setProperties(resetAllBedsToAvailable(realProperties))
    setApplicants([])
    setResidents([])
    setInvoices([])
    setReceipts([])
    setComplaints([])
    setSettlements([])
    setNotifications([])
    setActiveResidentId('')

    try {
      const keysToRemove = [
        'ch24_pg_properties_prod',
        'ch24_pg_applicants_prod',
        'ch24_pg_residents_prod',
        'ch24_pg_invoices_prod',
        'ch24_pg_receipts_prod',
        'ch24_pg_complaints_prod',
        'ch24_pg_settlements_prod',
        'ch24_pg_notifications_prod',
        'ch24_pg_active_resident_id_prod',
        'ch24_read_notifs'
      ]
      keysToRemove.forEach(k => localStorage.removeItem(k))
    } catch (e) {
      console.warn(e)
    }
  }

  const resetToDemoData = clearAllData


  // Helper: Update Bed Status in Inventory
  const updateBedStatusInInventory = (bedId, status, residentId = null) => {
    setProperties(prevProps =>
      prevProps.map(prop => ({
        ...prop,
        flats: prop.flats.map(flat => ({
          ...flat,
          rooms: flat.rooms.map(room => ({
            ...room,
            beds: room.beds.map(bed => {
              if (bed.bedId === bedId) {
                return { ...bed, status, residentId }
              }
              return bed
            })
          }))
        }))
      }))
    )
  }

  // -------------------------------------------------------------
  // 1. APPLICATION & MULTI-DOCUMENT KYC METHODS
  // -------------------------------------------------------------
  const submitApplication = (formData) => {
    const newId = `APP-2026-${Math.floor(100 + Math.random() * 900)}`
    const newApplicant = {
      id: newId,
      ...formData,
      status: 'PENDING_VERIFICATION',
      appliedAt: new Date().toISOString(),
      kycChecklist: {
        aadhaarVerified: false,
        panVerified: false,
        companyIdVerified: false,
        photoVerified: false,
        officeAddressVerified: false,
        guardianCrossCheckVerified: false,
        guardianPhoneCalled: false,
        guardianCallRemarks: '',
        adminRemarks: 'Application submitted online. Ready for document verification.'
      }
    }

    // Persist to MySQL database backend
    try {
      fetch('/api/applications.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          id: newId
        })
      }).catch(err => console.warn('Sync application note:', err))
    } catch (e) {
      console.warn('Sync application note:', e)
    }

    setApplicants(prev => [newApplicant, ...prev])

    // Add Notification
    addNotification({
      title: 'New Application Received',
      message: `${formData.fullName} applied for ${formData.preferredSociety || 'PG stay'}.`,
      type: 'APPLICATION',
      target: 'ADMIN'
    })

    return newApplicant
  }

  const updateKycChecklist = (applicantId, checklistUpdates) => {
    try {
      fetch('/api/applications.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: applicantId,
          action: 'update_checklist',
          kycChecklist: checklistUpdates
        })
      }).catch(err => console.warn('Update checklist note:', err))
    } catch (e) {
      console.warn('Update checklist note:', e)
    }

    setApplicants(prev =>
      prev.map(app => {
        if (app.id === applicantId) {
          return {
            ...app,
            kycChecklist: {
              ...app.kycChecklist,
              ...checklistUpdates
            }
          }
        }
        return app
      })
    )
  }

  const verifyApplicant = (applicantId, remarks = '') => {
    try {
      fetch('/api/applications.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: applicantId,
          action: 'verify_all',
          remarks
        })
      }).catch(err => console.warn('Verify applicant note:', err))
    } catch (e) {
      console.warn('Verify applicant note:', e)
    }

    setApplicants(prev =>
      prev.map(app => {
        if (app.id === applicantId) {
          return {
            ...app,
            status: 'VERIFIED',
            kycChecklist: {
              ...app.kycChecklist,
              aadhaarVerified: true,
              panVerified: true,
              companyIdVerified: true,
              photoVerified: true,
              officeAddressVerified: true,
              guardianCrossCheckVerified: true,
              guardianPhoneCalled: true,
              adminRemarks: remarks || 'All 6 KYC documents verified and guardian cross-checked.'
            }
          }
        }
        return app
      })
    )
  }

  const approveApplicant = (applicantId) => {
    try {
      fetch('/api/applications.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: applicantId,
          action: 'approve'
        })
      }).catch(err => console.warn('Approve applicant note:', err))
    } catch (e) {
      console.warn('Approve applicant note:', e)
    }

    setApplicants(prev =>
      prev.map(app => (app.id === applicantId ? { ...app, status: 'APPROVED' } : app))
    )
  }

  const rejectApplicant = (applicantId, reason) => {
    try {
      fetch('/api/applications.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: applicantId,
          action: 'reject',
          reason
        })
      }).catch(err => console.warn('Reject applicant note:', err))
    } catch (e) {
      console.warn('Reject applicant note:', e)
    }

    setApplicants(prev =>
      prev.map(app =>
        app.id === applicantId
          ? {
            ...app,
            status: 'REJECTED',
            kycChecklist: { ...app.kycChecklist, adminRemarks: `Rejected: ${reason}` }
          }
          : app
      )
    )
  }

  const requestKycResubmission = (applicantId, remarks) => {
    try {
      fetch('/api/applications.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: applicantId,
          action: 'request_resubmission',
          remarks
        })
      }).catch(err => console.warn('Request resubmission note:', err))
    } catch (e) {
      console.warn('Request resubmission note:', e)
    }

    setApplicants(prev =>
      prev.map(app =>
        app.id === applicantId
          ? {
            ...app,
            status: 'DOCS_PENDING',
            kycChecklist: { ...app.kycChecklist, adminRemarks: remarks }
          }
          : app
      )
    )
  }

  // -------------------------------------------------------------
  // 2. ROOM & BED ALLOCATION & ONBOARDING
  // -------------------------------------------------------------
  const allocateBed = ({
    applicantId,
    propertyId,
    societyName,
    flatNumber,
    flatType,
    roomId,
    roomType,
    bedId,
    bedNumber,
    rentAmount,
    securityDeposit,
    moveInDate
  }) => {
    const applicant = applicants.find(a => a.id === applicantId)
    if (!applicant) return false

    const newResidentId = `RES-${String(residents.length + 1).padStart(3, '0')}`

    const newResident = {
      id: newResidentId,
      fullName: applicant.fullName,
      email: applicant.email,
      phone: applicant.phone,
      dob: applicant.dob,
      bloodGroup: applicant.bloodGroup,
      photoUrl: applicant.documents?.photoUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=800',
      permanentAddress: applicant.permanentAddress,
      propertyId,
      societyName,
      flatNumber,
      flatType,
      roomId,
      roomType,
      bedId,
      bedNumber,
      moveInDate: moveInDate || applicant.moveInDate || new Date().toISOString().split('T')[0],
      rentAmount: Number(rentAmount),
      securityDeposit: Number(securityDeposit),
      depositPaid: Number(securityDeposit),
      status: 'ACTIVE',
      wifiPassword: `Chapter24@${societyName.replace(/\s+/g, '')}${flatNumber.replace(/\D/g, '')}`,
      gateCurfew: '10:30 PM',
      workplaceType: applicant.workplaceType,
      companyName: applicant.companyName,
      designation: applicant.designation,
      employeeId: applicant.employeeId,
      officeAddress: applicant.officeAddress,
      guardianName: applicant.guardianName,
      guardianRelation: applicant.guardianRelation,
      guardianPhone: applicant.guardianPhone,
      guardianEmail: applicant.guardianEmail,
      guardianAddress: applicant.guardianAddress,
      documents: applicant.documents,
      moveInChecklist: {
        keysHandedOver: true,
        mattressVerified: true,
        wardrobeLockWorking: true,
        meterReadingConfirmed: true,
        agreementSigned: true,
        completedDate: new Date().toISOString().split('T')[0]
      }
    }

    // 1. Add to residents
    setResidents(prev => [newResident, ...prev])

    // 2. Mark applicant as ALLOCATED
    setApplicants(prev =>
      prev.map(app => (app.id === applicantId ? { ...app, status: 'ALLOCATED' } : app))
    )

    // 3. Mark bed as OCCUPIED in properties inventory
    updateBedStatusInInventory(bedId, 'Occupied', newResidentId)

    // 4. Generate initial Security Deposit + 1st Month Rent Invoice
    const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' })
    const newInvoice = {
      id: `INV-2026-${Math.floor(1000 + Math.random() * 9000)}`,
      invoiceNumber: `CH24-INV-${Date.now().toString().slice(-6)}`,
      residentId: newResidentId,
      residentName: newResident.fullName,
      residentEmail: newResident.email,
      residentPhone: newResident.phone,
      societyName: newResident.societyName,
      flatNumber: newResident.flatNumber,
      roomType: newResident.roomType,
      bedNumber: newResident.bedNumber,
      month: currentMonth,
      billingDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'PENDING',
      lineItems: [
        { description: `First Month Room Rent (${currentMonth})`, amount: Number(rentAmount) },
        { description: 'One-Time Refundable Security Deposit', amount: Number(securityDeposit) },
        { description: 'Maintenance & Housekeeping Access', amount: 500 }
      ],
      subTotal: Number(rentAmount) + Number(securityDeposit) + 500,
      gstAmount: 0,
      totalAmount: Number(rentAmount) + Number(securityDeposit) + 500,
      paidAmount: 0,
      paymentDate: null,
      paymentMode: null,
      transactionId: null,
      receiptId: null
    }

    setInvoices(prev => [newInvoice, ...prev])

    addNotification({
      title: 'Bed Allocation Completed',
      message: `${newResident.fullName} allocated to ${bedNumber}, ${flatNumber}, ${societyName}.`,
      type: 'ALLOCATION',
      target: 'ALL'
    })

    return newResident
  }

  const completeMoveIn = (residentId, checklist) => {
    setResidents(prev =>
      prev.map(res => {
        if (res.id === residentId) {
          return {
            ...res,
            moveInChecklist: {
              ...res.moveInChecklist,
              ...checklist,
              completedDate: new Date().toISOString().split('T')[0]
            }
          }
        }
        return res
      })
    )
  }

  // -------------------------------------------------------------
  // 3. BILLING, INVOICE GENERATOR, PAYMENTS & RECEIPTS
  // -------------------------------------------------------------
  const generateMonthlyInvoices = (monthName = 'September 2026') => {
    const activeResidents = residents.filter(r => r.status === 'ACTIVE' || r.status === 'NOTICE_SERVED')
    const newGeneratedInvoices = []

    activeResidents.forEach(res => {
      // Check if invoice already exists for this resident and month
      const exists = invoices.some(inv => inv.residentId === res.id && inv.month === monthName)
      if (!exists) {
        const invId = `INV-2026-${Math.floor(1000 + Math.random() * 9000)}`
        const electricityUnits = Math.floor(8 + Math.random() * 15)
        const electricityCost = electricityUnits * 40
        const gasCost = 350
        const housekeepingCost = 450
        const wifi = 200
        const rent = res.rentAmount || 7000

        const inv = {
          id: invId,
          invoiceNumber: `CH24-INV-${Date.now().toString().slice(-6)}-${res.id.slice(-3)}`,
          residentId: res.id,
          residentName: res.fullName,
          residentEmail: res.email,
          residentPhone: res.phone,
          societyName: res.societyName,
          flatNumber: res.flatNumber,
          roomType: res.roomType,
          bedNumber: res.bedNumber,
          month: monthName,
          billingDate: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          status: 'PENDING',
          lineItems: [
            { description: `Monthly Room Rent (${monthName})`, category: 'Room Accommodation', amount: rent },
            { description: 'Piped Cooking Gas & LPG Facility Charges', category: 'Gas Utility', amount: gasCost },
            { description: 'Daily Housekeeping, Room Cleaning & Waste Upkeep', category: 'Housekeeping & Sanitization', amount: housekeepingCost },
            { description: `Electricity Unit Consumption (${electricityUnits} units)`, category: 'Power & Electricity', amount: electricityCost },
            { description: 'High-Speed Wi-Fi & Power Backup', category: 'Amenities & Connectivity', amount: wifi }
          ],
          subTotal: rent + gasCost + housekeepingCost + wifi + electricityCost,
          gstAmount: 0,
          totalAmount: rent + gasCost + housekeepingCost + wifi + electricityCost,
          paidAmount: 0,
          paymentDate: null,
          paymentMode: null,
          transactionId: null,
          receiptId: null
        }
        newGeneratedInvoices.push(inv)
      }
    })

    if (newGeneratedInvoices.length > 0) {
      setInvoices(prev => [...newGeneratedInvoices, ...prev])
      addNotification({
        title: `${monthName} Rent Cycle Generated`,
        message: `Generated ${newGeneratedInvoices.length} invoices for all active residents with Gas, Housekeeping, & Electricity billing.`,
        type: 'BILLING',
        target: 'ALL_RESIDENTS'
      })
    }

    return newGeneratedInvoices.length
  }

  const createCustomInvoice = (residentId, { month, description, amount, dueDate }) => {
    const res = residents.find(r => r.id === residentId)
    if (!res) return null

    const newInv = {
      id: `INV-2026-${Math.floor(1000 + Math.random() * 9000)}`,
      invoiceNumber: `CH24-INV-AD-${Date.now().toString().slice(-5)}`,
      residentId: res.id,
      residentName: res.fullName,
      residentEmail: res.email,
      residentPhone: res.phone,
      societyName: res.societyName,
      flatNumber: res.flatNumber,
      roomType: res.roomType,
      bedNumber: res.bedNumber,
      month: month || 'Ad-hoc / Incidentals',
      billingDate: new Date().toISOString().split('T')[0],
      dueDate: dueDate || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'PENDING',
      lineItems: [{ description, amount: Number(amount) }],
      subTotal: Number(amount),
      gstAmount: 0,
      totalAmount: Number(amount),
      paidAmount: 0,
      paymentDate: null,
      paymentMode: null,
      transactionId: null,
      receiptId: null
    }

    setInvoices(prev => [newInv, ...prev])
    return newInv
  }

  const recordPayment = (invoiceId, { paymentMode, transactionId, amountPaid }) => {
    const invoice = invoices.find(inv => inv.id === invoiceId)
    if (!invoice) return null

    const receiptId = `RCP-2026-${Math.floor(100 + Math.random() * 900)}`
    const receiptNumber = `CH24-RCP-${Date.now().toString().slice(-6)}`
    const paidAmt = Number(amountPaid) || invoice.totalAmount
    const txnId = transactionId || `UPI/${Math.floor(100000000000 + Math.random() * 900000000000)}/CH24`

    // 1. Create Receipt with full itemized line items (Rent, Gas, Housekeeping, Electricity)
    const newReceipt = {
      id: receiptId,
      receiptNumber,
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      residentId: invoice.residentId,
      residentName: invoice.residentName,
      societyName: invoice.societyName,
      flatNumber: invoice.flatNumber,
      roomType: invoice.roomType,
      bedNumber: invoice.bedNumber,
      month: invoice.month,
      lineItems: invoice.lineItems || [],
      amountPaid: paidAmt,
      paymentDate: new Date().toISOString(),
      paymentMode: paymentMode || 'UPI / QR',
      transactionId: txnId,
      receivedBy: 'Chapter 24 Accounts Desk',
      remarks: `Received towards ${invoice.month} rent & utility charges (Gas, Housekeeping, Electricity).`
    }

    // 2. Update Invoice
    setInvoices(prev =>
      prev.map(inv => {
        if (inv.id === invoiceId) {
          return {
            ...inv,
            status: 'PAID',
            paidAmount: paidAmt,
            paymentDate: new Date().toISOString().split('T')[0],
            paymentMode: paymentMode || 'UPI / QR',
            transactionId: txnId,
            receiptId
          }
        }
        return inv
      })
    )

    // 3. Add to Receipts
    setReceipts(prev => [newReceipt, ...prev])

    addNotification({
      title: 'Payment Received',
      message: `₹${paidAmt} received from ${invoice.residentName} for ${invoice.month}.`,
      type: 'PAYMENT',
      target: 'ADMIN'
    })

    return newReceipt
  }

  // -------------------------------------------------------------
  // 4. COMPLAINTS & TICKETING
  // -------------------------------------------------------------
  const submitComplaint = ({ residentId, category, priority, title, description, photoUrl }) => {
    const res = residents.find(r => r.id === residentId) || currentResident
    const ticketNumber = `TKT-${Math.floor(800 + complaints.length + 1)}`
    const newTicket = {
      id: `TKT-2026-${Math.floor(1000 + Math.random() * 9000)}`,
      ticketNumber,
      residentId: res.id,
      residentName: res.fullName,
      phone: res.phone,
      societyName: res.societyName,
      flatNumber: res.flatNumber,
      category: category || 'General',
      priority: priority || 'Medium',
      title,
      description,
      photoUrl: photoUrl || null,
      status: 'OPEN',
      createdAt: new Date().toISOString(),
      assignedTo: 'Unassigned',
      adminRemarks: 'Ticket logged. Support team will assign technician shortly.',
      resolutionNotes: null,
      resolvedAt: null
    }

    setComplaints(prev => [newTicket, ...prev])

    addNotification({
      title: 'New Maintenance Ticket',
      message: `${res.fullName} (${res.societyName}): ${title}`,
      type: 'COMPLAINT',
      target: 'ADMIN'
    })

    return newTicket
  }

  const updateComplaintStatus = (ticketId, { status, assignedTo, adminRemarks, resolutionNotes }) => {
    setComplaints(prev =>
      prev.map(tkt => {
        if (tkt.id === ticketId) {
          const isResolving = status === 'RESOLVED' || status === 'CLOSED'
          return {
            ...tkt,
            status: status || tkt.status,
            assignedTo: assignedTo !== undefined ? assignedTo : tkt.assignedTo,
            adminRemarks: adminRemarks !== undefined ? adminRemarks : tkt.adminRemarks,
            resolutionNotes: resolutionNotes !== undefined ? resolutionNotes : tkt.resolutionNotes,
            resolvedAt: isResolving ? new Date().toISOString() : tkt.resolvedAt
          }
        }
        return tkt
      })
    )
  }

  // -------------------------------------------------------------
  // 5. MOVE-OUT & FINAL SETTLEMENT
  // -------------------------------------------------------------
  const submitMoveOutNotice = (residentId, { requestedVacateDate, reason, refundBankName, refundAccountNumber, refundIfscCode, refundUpiId }) => {
    const res = residents.find(r => r.id === residentId)
    if (!res) return null

    // 1. Update Resident Status
    setResidents(prev =>
      prev.map(r =>
        r.id === residentId
          ? {
            ...r,
            status: 'NOTICE_SERVED',
            noticeDetails: {
              noticeDate: new Date().toISOString().split('T')[0],
              requestedVacateDate,
              reason,
              refundBankName,
              refundAccountNumber,
              refundIfscCode,
              refundUpiId
            }
          }
          : r
      )
    )

    // 2. Create Settlement Case
    const newSettlement = {
      id: `SET-2026-${Math.floor(100 + Math.random() * 900)}`,
      residentId: res.id,
      residentName: res.fullName,
      phone: res.phone,
      societyName: res.societyName,
      flatNumber: res.flatNumber,
      bedId: res.bedId,
      bedNumber: res.bedNumber,
      securityDeposit: res.securityDeposit || 14000,
      noticeDate: new Date().toISOString().split('T')[0],
      vacateDate: requestedVacateDate,
      status: 'INSPECTION_PENDING',
      deductions: [
        { item: 'Deep Cleaning & Repainting Pro-rata', amount: 1000 },
        { item: 'Final Electricity & Utility Adjustment', amount: 400 },
        { item: 'Unpaid Dues / Maintenance', amount: 0 }
      ],
      totalDeductions: 1400,
      netRefundAmount: (res.securityDeposit || 14000) - 1400,
      refundBankName: refundBankName || '',
      refundAccountNumber: refundAccountNumber || '',
      refundIfscCode: refundIfscCode || '',
      refundUpiId: refundUpiId || '',
      inspectionRemarks: 'Notice acknowledged. Room inspection scheduled upon vacate date.',
      refundTransactionId: null,
      settledDate: null
    }

    setSettlements(prev => [newSettlement, ...prev])

    addNotification({
      title: 'Move-Out Notice Submitted',
      message: `${res.fullName} requested move-out on ${requestedVacateDate}.`,
      type: 'MOVEOUT',
      target: 'ADMIN'
    })

    return newSettlement
  }

  const updateSettlementInspection = (settlementId, { deductions, inspectionRemarks, status = 'APPROVED_FOR_REFUND' }) => {
    setSettlements(prev =>
      prev.map(set => {
        if (set.id === settlementId) {
          const totalDeductions = deductions.reduce((acc, curr) => acc + Number(curr.amount || 0), 0)
          const netRefund = Math.max(0, set.securityDeposit - totalDeductions)
          return {
            ...set,
            deductions,
            totalDeductions,
            netRefundAmount: netRefund,
            inspectionRemarks: inspectionRemarks || set.inspectionRemarks,
            status
          }
        }
        return set
      })
    )
  }

  const finalizeSettlementAndRefund = (settlementId, refundTxnId) => {
    const settlement = settlements.find(s => s.id === settlementId)
    if (!settlement) return false

    const txn = refundTxnId || `BANK/REFUND/${Date.now()}`

    // 1. Mark Settlement Closed
    setSettlements(prev =>
      prev.map(s =>
        s.id === settlementId
          ? {
            ...s,
            status: 'REFUNDED_CLOSED',
            refundTransactionId: txn,
            settledDate: new Date().toISOString().split('T')[0]
          }
          : s
      )
    )

    // 2. Mark Resident as SETTLED / MOVED_OUT
    setResidents(prev =>
      prev.map(r => (r.id === settlement.residentId ? { ...r, status: 'SETTLED' } : r))
    )

    // 3. Release the Bed back to AVAILABLE in properties inventory
    if (settlement.bedId) {
      updateBedStatusInInventory(settlement.bedId, 'Available', null)
    }

    addNotification({
      title: 'Deposit Refund Processed',
      message: `Refund of ₹${settlement.netRefundAmount} transferred to ${settlement.residentName}. Bed ${settlement.bedNumber} is now Available.`,
      type: 'REFUND',
      target: 'ALL'
    })

    return true
  }

  // -------------------------------------------------------------
  // 6. NOTIFICATIONS
  // -------------------------------------------------------------
  const addNotification = ({ title, message, type = 'INFO', target = 'ALL' }) => {
    const newNotif = {
      id: `NOTIF-${Date.now()}`,
      title,
      message,
      type,
      target,
      timestamp: new Date().toISOString()
    }
    setNotifications(prev => [newNotif, ...prev])
    apiService.addNotification(newNotif).catch(e => console.warn('Notification sync note:', e))
  }

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
    apiService.deleteNotification(id).catch(e => console.warn('Notification delete note:', e))
  }

  const clearNotifications = (targetRole = null) => {
    if (!targetRole) {
      setNotifications([])
      apiService.clearAllNotifications().catch(e => console.warn('Notification clear note:', e))
    } else {
      setNotifications(prev => prev.filter(n => {
        if (targetRole === 'ADMIN') return n.target === 'RESIDENT'
        if (targetRole === 'RESIDENT') return n.target === 'ADMIN'
        return false
      }))
    }
  }


  return (
    <PGManagementContext.Provider
      value={{
        // Data States
        properties,
        applicants,
        residents,
        invoices,
        receipts,
        complaints,
        settlements,
        notifications,
        activeResidentId,
        currentResident,
        isDataLoading,
        refreshData,

        // Setters & Actions
        setActiveResidentId,
        clearAllData,
        resetToDemoData,
        updateBedStatusInInventory,

        // 1. Application & KYC
        submitApplication,
        updateKycChecklist,
        verifyApplicant,
        approveApplicant,
        rejectApplicant,
        requestKycResubmission,

        // 2. Allocation & Move-In
        allocateBed,
        completeMoveIn,

        // 3. Billing & Payments
        generateMonthlyInvoices,
        createCustomInvoice,
        recordPayment,

        // 4. Complaints
        submitComplaint,
        updateComplaintStatus,

        // 5. Move-Out & Settlements
        submitMoveOutNotice,
        updateSettlementInspection,
        finalizeSettlementAndRefund,

        // 6. Notifications
        addNotification,
        deleteNotification,
        clearNotifications
      }}
    >
      {children}
    </PGManagementContext.Provider>
  )
}

export const usePG = () => {
  const context = useContext(PGManagementContext)
  if (!context) {
    throw new Error('usePG must be used within a PGManagementProvider')
  }
  return context
}

export default PGManagementContext
